import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../LanguageContext';
import { getClosureTasks, updateClosureTask } from '../api';
import { projLabel } from '../components/UnitsDeals/constants';

// ── My closure tasks ──
// Each internal member sees ONLY the deal-closure checklist steps assigned to them
// (as the deal's closer, or a named step assignee) and can mark each Yes/No. The server
// scopes every read + re-checks ownership on every update.
export default function ClosureTasks() {
  const { t, lang } = useLanguage();
  const L = (a, e) => (lang === 'ar' ? a : e);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    const res = await getClosureTasks();
    if (res.error) { setError(true); setDeals([]); }
    else setDeals(res.deals || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStep = async (unitId, index, value) => {
    setDeals(prev => prev.map(d => d.unitId === unitId
      ? { ...d, steps: d.steps.map(s => s.index === index ? { ...s, value } : s) }
      : d));
    const res = await updateClosureTask(unitId, index, value);
    if (res.error) load(); // revert to server truth on failure
  };

  const doneOf = (d) => d.steps.filter(s => s.optional ? (s.value === 'yes' || s.value === 'no') : s.value === 'yes').length;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: '1.3rem', fontWeight: 800, margin: 0 }}>⭐ {L('مهام الإغلاق', 'My Closure Tasks')}</h1>
        <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: 4 }}>
          {L('خطوات إغلاق الصفقات المُسندة إليك فقط — حدّث حالة كل خطوة.', 'Only the deal-closure steps assigned to you — update each step.')}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>{t('loading') || '…'}</div>
      ) : error ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--error)' }}>
          {L('تعذّر التحميل', 'Failed to load')} · <button className="btn btn-secondary btn-sm" onClick={load}>{L('إعادة المحاولة', 'Retry')}</button>
        </div>
      ) : deals.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', border: '1px dashed var(--border)', borderRadius: 12 }}>
          {L('لا توجد مهام إغلاق مُسندة إليك حاليًا.', 'You have no closure tasks assigned right now.')}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          {deals.map(d => {
            const done = doneOf(d);
            const complete = done === d.total;
            return (
              <div key={d.unitId} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>{d.clientName || L('عميل', 'Client')}
                      {d.clientPhone && <span style={{ fontWeight: 500, fontSize: '0.78rem', color: 'var(--text-secondary)', marginInlineStart: 8, direction: 'ltr' }}>{d.clientPhone}</span>}
                    </div>
                    <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                      <span style={{ color: '#8b5cf6', fontWeight: 600 }}>{projLabel(d.project, t)}</span>
                      {' · '}{L('وحدة', 'Unit')} {d.unitCode}{d.unitType ? ` · ${d.unitType}` : ''}
                      {d.paymentMethod ? ` · ${d.paymentMethod}` : ''}
                    </div>
                  </div>
                  <span style={{ fontSize: '0.72rem', fontWeight: 800, padding: '3px 10px', borderRadius: 12, color: '#fff', background: complete ? 'var(--success)' : 'var(--accent)' }}>
                    {done}/{d.total}{complete ? ` ✓` : ''}
                  </span>
                </div>
                <div style={{ padding: '6px 14px 12px' }}>
                  {d.steps.map(s => (
                    <div key={s.index} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ flex: 1, minWidth: 0, fontSize: '0.84rem', fontWeight: 600 }}>
                        {lang === 'ar' ? s.ar : s.en}
                        {s.optional && <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginInlineStart: 6, fontWeight: 500 }}>({L('اختياري', 'optional')})</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        {[['yes', L('نعم', 'Yes'), 'var(--success)'], ['no', L('لا', 'No'), 'var(--error)']].map(([v, label, col]) => (
                          <button key={v} type="button" onClick={() => setStep(d.unitId, s.index, s.value === v ? '' : v)}
                            style={{
                              padding: '5px 14px', borderRadius: 8, fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer',
                              border: `1px solid ${s.value === v ? col : 'var(--border)'}`,
                              background: s.value === v ? col : 'transparent',
                              color: s.value === v ? '#fff' : 'var(--text-secondary)',
                            }}>{label}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
